import React, { useEffect, useMemo, useState } from 'react';
import { dispatchCusEvent, getOnlyData } from '../../../utils';
import {
  dayList,
  ENUM_CUS_EVENT,
  ENUM_LOCALSTORAGE,
  ENUM_SLIDER_LINK,
  TEST_MODE
} from '../../../enums/enums';
import { useAppSelector } from '../../../store';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { setSliderBanner, setSliderBannerGroup } from '../../../store/apps/groupInfoSlice';
import { API_GROUP } from '../../../api/user';
import dayjs from 'dayjs';
import useToast from '../../useToast';
import { mockSliderBanner } from '../../../enums/mock';
import { useLocalStorage } from 'usehooks-ts';

export default function useEditSliderBanner({ groupId, bannerId, type, group = {} }) {
  const defaultCurrentBanner = {
    groupId: groupId,
    enabled: true,
    name: '',
    position: 1,
    language: group?.langList?.[0],
    gameId: 0,
    makeBotLink: false,
    relativeUrl: undefined,
    externalUrl: undefined,
    bannerText: undefined,
    fileName: undefined,
    fileData: undefined,
    mobileFileName: undefined,
    mobileFileData: undefined,
    dateFrom: getOnlyData(dayjs(), '00:00:00'),
    dateTo: getOnlyData(dayjs().add(7, 'day'), '23:59:59'),
    weekDays: [true, true, true, true, true, true, true]
  };

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { successToast } = useToast();
  const banners = useAppSelector((state) => state.groupInfo.sliderBanner?.[groupId]) || [];
  const refLinksInfo = useAppSelector((state) => state.groupInfo.refLinksInfo?.[groupId]) || {};
  const [languageByGroup, setBannerLanguagesHash] = useLocalStorage(
    ENUM_LOCALSTORAGE.bannerLanguagesHash,
    {}
  );
  const tgBot = refLinksInfo.botName || '';

  const defaultLangMemo = useMemo(() => {
    return languageByGroup[groupId] || 'all';
  }, [groupId]);

  const [currentBanner, setCurrentBanner] = useState(defaultCurrentBanner);
  const [loader, setLoader] = useState(type == 'list');
  const [onlyUnread, setOnlyUnread] = useState(true);
  const [filterLang, setFilterLang] = useState(defaultLangMemo);

  const updateSliders = () => {
    if (TEST_MODE) {
      setTimeout(() => {
        dispatch(setSliderBanner({ 1: mockSliderBanner, 10: mockSliderBanner }));
        setLoader(false);
      }, 3_000);
      return;
    }

    setLoader(true);
    API_GROUP.slider
      .list({
        groupId: +groupId,
        activeOnly: onlyUnread,
        lang: filterLang == 'all' ? null : filterLang
      })
      .then((e) => {
        const list = e.data.list;
        dispatch(setSliderBannerGroup({ data: list, groupId }));
      })
      .catch(console.log)
      .finally(() => setLoader(false));
  };

  useEffect(() => {
    if (filterLang == defaultLangMemo) return;

    languageByGroup[groupId] = filterLang;
    setBannerLanguagesHash(languageByGroup);
  }, [filterLang]);

  useEffect(() => {
    if (type == 'list') {
      updateSliders();
      return;
    }
    if (banners.length) return;

    updateSliders();
  }, [type, filterLang, onlyUnread]);

  useEffect(() => {
    if (tgBot) return;
    if (TEST_MODE) return;
    if (type == 'list') return;

    dispatchCusEvent(ENUM_CUS_EVENT.getRefcodeList, { groupId });
  }, [tgBot, type]);

  useEffect(() => {
    if (!banners.length) return;
    if (!bannerId) return;
    if (bannerId == 'new') return;
    setCurrentBanner(banners.find((el) => el.id == bannerId) || defaultCurrentBanner);
  }, [bannerId, banners]);

  const addBanner = () => {
    navigate(`/slider-banners/${groupId}/new`);
  };

  const onBannerDelete = (bannerId) => {
    API_GROUP.slider
      .delete({ bannerId })
      .then(() => {
        const newBanners = banners.filter((el) => el.id !== bannerId);
        dispatch(setSliderBannerGroup({ groupId: groupId, data: newBanners }));
      })
      .catch(console.log);
  };

  const createBanner = (bannerInfo) => {
    return API_GROUP.slider
      .create(bannerInfo)
      .then((e) => {
        const bannerId = e.data.bannerId;
        successToast('New banner added');
        if (bannerId) {
          navigate(`/slider-banners/${groupId}/${bannerId}`);
        } else {
          navigate(`/slider-banners/${groupId}/list`);
        }
      })
      .catch(console.log);
  };

  const updateBanner = (bannerInfo) => {
    return API_GROUP.slider
      .update({ ...bannerInfo, bannerId })
      .then((e) => {
        successToast('Information updated successfully');
        // navigate(`/slider-banners/${groupId}/${bannerId}`);
      })
      .catch(console.log);
  };

  const saveBanner = async (bannerInfo) => {
    if (isNew) {
      createBanner(bannerInfo);
      return;
    }

    await updateBanner(bannerInfo);

    console.log('bannerInfo', bannerInfo);

    // if (isNew) {
    //   dispatch(setSliderBannerGroup({ groupId: groupId, data: [...banners, bannerInfo] }));
    //   return;
    // }
    // const newBanners = [];
    // for (let banner of banners) {
    //   banner.id == bannerId ? newBanners.push(bannerInfo) : newBanners.push(banner);
    // }
    // dispatch(setSliderBannerGroup({ groupId: groupId, data: newBanners }));
    // запрос на сервер и в ответе
  };

  const isNew = bannerId == 'new';
  const title = isNew
    ? 'Creating a new banner'
    : banners.find((el) => el.id == bannerId)?.name || 'Edit banner';
  const days = dayList.map((el, indx) => ({ label: el.title, value: indx }));
  const bannerTitle = isNew ? 'Creating a new banner' : 'Edit banner';

  return {
    banners,
    currentBanner,
    title,
    days,
    isNew,
    bannerTitle,
    tgBot,
    loader,
    onlyUnread,
    filterLang,
    setFilterLang,
    setOnlyUnread,
    updateSliders,
    setCurrentBanner,
    addBanner,
    onBannerDelete,
    saveBanner
  };
}
